import React, { useCallback, useEffect, useState } from 'react';
import { horizonInstancesService } from '../../services/api';
import { HorizonInstance, HorizonInstanceHost } from '../../types';
import { formatDateTime } from '../../utils/datetime';

// Which Horizon clusters may reach this platform.
//
// The unit is a CLUSTER, not a host. NetSapiens replicates the Registered App
// row between peer nodes — callback URL and secret included — so every node
// signs with the same secret and any of them may send a sign-in webhook,
// concurrently, for the same or different users. So the secret lives once, on
// the cluster, and the nodes are listed under it.
//
// A node's own source addresses are checked when its webhook arrives; requests
// afterwards are checked against the union of the cluster's, because a user's
// page is served by whichever peer they reached.

const nf = new Intl.NumberFormat();

function ipsOf(v: string): string[] {
  return v.split(',').map(x => x.trim()).filter(Boolean);
}

const HorizonInstancesPanel: React.FC = () => {
  const [instances, setInstances] = useState<HorizonInstance[]>([]);
  const [requireOwnSecret, setRequireOwnSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newCluster, setNewCluster] = useState('');
  const [newHost, setNewHost] = useState('');
  const [newIps, setNewIps] = useState('');

  const [revealed, setRevealed] = useState<number | null>(null);
  const [showTokens, setShowTokens] = useState<number | null>(null);
  const [hostForm, setHostForm] = useState<
    { instanceId: number; id?: number; hostname: string; ips: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await horizonInstancesService.list();
      setInstances(res.instances);
      setRequireOwnSecret(res.require_own_secret);
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'Horizon instances are managed by Super Users only.'
        : 'Could not load Horizon instances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyText = async (text: string) => {
    try { await navigator.clipboard.writeText(text); }
    catch { window.prompt('Copy:', text); }
  };

  const fail = (e: any, fallback: string) =>
    setError(e?.response?.data?.error || fallback);

  const add = async () => {
    if (!newLabel.trim()) return;
    try {
      await horizonInstancesService.create({
        label: newLabel.trim(),
        cluster_name: newCluster.trim() || undefined,
        hostname: newHost.trim() || undefined,
        allowed_from_ips: ipsOf(newIps),
      });
      setAdding(false);
      setNewLabel(''); setNewCluster(''); setNewHost(''); setNewIps('');
      await load();
    } catch (e) { fail(e, 'Could not register the cluster'); }
  };

  const saveHost = async () => {
    if (!hostForm?.hostname.trim()) return;
    try {
      await horizonInstancesService.saveHost({
        instance_id: hostForm.instanceId,
        hostname: hostForm.hostname.trim(),
        allowed_from_ips: ipsOf(hostForm.ips),
      });
      setHostForm(null);
      await load();
    } catch (e) { fail(e, 'Could not save the node'); }
  };

  const removeHost = async (h: HorizonInstanceHost) => {
    try {
      await horizonInstancesService.deleteHost(h.id);
      await load();
    } catch (e: any) {
      if (e?.response?.data?.needs_confirm) {
        if (window.confirm(`${e.response.data.error}\n\nRemove it anyway?`)) {
          await horizonInstancesService.deleteHost(h.id, true);
          await load();
        }
        return;
      }
      fail(e, 'Could not remove the node');
    }
  };

  const generateSecret = async (i: HorizonInstance) => {
    // Every peer replicates this row, so a new secret stops the WHOLE cluster
    // signing in until it is pasted back — not just one node.
    if (i.callback_secret && !window.confirm(
      `${i.label} already has a callback secret.\n\n`
      + `A new one stops sign-ins through all ${i.hosts.length} of its nodes until you `
      + 'paste the new value into that Horizon’s Registered Apps page. Continue?')) {
      return;
    }
    try {
      const res = await horizonInstancesService.update(i.id, { generate_callback_secret: true });
      await load();
      setRevealed(i.id);
      if (res.callback_secret) await copyText(res.callback_secret);
    } catch (e) { fail(e, 'Could not generate a callback secret'); }
  };

  const setStatus = async (i: HorizonInstance, status: 'active' | 'disabled') => {
    try {
      await horizonInstancesService.update(i.id, { status });
      await load();
    } catch (e) { fail(e, 'Could not change the cluster'); }
  };

  const broken = instances.filter(i => !i.callback_secret || i.hosts.length === 0);

  return (
    <div className="settings-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>Horizon Instances</h2>
        <button type="button" onClick={() => setAdding(a => !a)}>
          {adding ? 'Cancel' : 'Register a cluster'}
        </button>
      </div>

      <p className="small">
        Registered per <strong>cluster</strong>: peer nodes replicate the Registered App
        row between them, so they share one callback secret and any of them may sign a
        user in. Each node is listed so its webhooks resolve here &mdash; nothing needs
        installing on the portals themselves.
      </p>

      {broken.length > 0 && (
        <div className="error-message">
          {broken.length} cluster{broken.length === 1 ? '' : 's'} cannot be identified, for
          want of a callback secret or any node. Sign-ins fall back to the shared{' '}
          <span className="monospace">HORIZON_CALLBACK_SECRET</span> and the tokens they mint
          name no cluster, so those requests cannot be attributed, disabled or pinned.
          {requireOwnSecret && ' This host REFUSES that fallback, so they are failing now.'}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {loading && <div className="small">Loading&hellip;</div>}

      {adding && (
        <div className="settings-panel" style={{ marginBottom: '14px' }}>
          <h3 style={{ marginTop: 0 }}>Register a cluster</h3>
          <div style={{ display: 'grid', gap: '8px', maxWidth: '520px' }}>
            <label className="small">Label
              <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
                placeholder="dw.nseng.dev lab" />
            </label>
            <label className="small">Cluster name &mdash; display only
              <input value={newCluster} onChange={e => setNewCluster(e.target.value)} />
            </label>
            <label className="small">
              First node &mdash; exactly the host Horizon knows itself by, since it is
              matched against the one in its own webhook. Add its peers afterwards.
              <input value={newHost} onChange={e => setNewHost(e.target.value)}
                placeholder="core1-phx.dw.nseng.dev" />
            </label>
            <label className="small">
              That node&rsquo;s source addresses, comma separated. Leave empty for any.
              <input value={newIps} onChange={e => setNewIps(e.target.value)}
                placeholder="132.226.155.215" />
            </label>
            <div>
              <button type="button" onClick={add} disabled={!newLabel.trim()}>
                Register and generate a callback secret
              </button>
            </div>
          </div>
        </div>
      )}

      {instances.map(i => (
        <div key={i.id} className="settings-panel" style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, marginRight: 'auto' }}>
              {i.label}
              {i.cluster_name && <span className="small" style={{ opacity: 0.6 }}> &middot; {i.cluster_name}</span>}
              {i.status === 'disabled' && (
                <span style={{ color: '#c0392b', fontSize: '0.8em' }}> disabled</span>
              )}
            </h3>
            {i.status === 'active'
              ? <button type="button" onClick={() => setStatus(i, 'disabled')}>Disable</button>
              : <button type="button" onClick={() => setStatus(i, 'active')}>Enable</button>}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap', marginTop: '8px' }}>
            <strong className="small" style={{ minWidth: '110px' }}>Callback secret</strong>
            {i.callback_secret ? (
              <>
                <span className="monospace small">
                  {revealed === i.id ? i.callback_secret : `${i.callback_secret.slice(0, 8)}…`}
                </span>
                <button type="button" onClick={() => setRevealed(revealed === i.id ? null : i.id)}>
                  {revealed === i.id ? 'Hide' : 'Show'}
                </button>
                <button type="button" onClick={() => copyText(i.callback_secret!)}>Copy</button>
              </>
            ) : <span className="small" style={{ color: '#c0392b' }}>not set</span>}
            <button type="button" onClick={() => generateSecret(i)}>
              {i.callback_secret ? 'Generate new' : 'Generate'}
            </button>
          </div>
          <p className="small" style={{ margin: '6px 0 10px', opacity: 0.7 }}>
            One secret for the whole cluster. Paste it into that Horizon&rsquo;s{' '}
            <em>Registered Apps &rarr; Callback secret</em>; replication carries it to the
            peers. Generate here, paste there, then test a sign-in &mdash; in between,
            no node of this cluster can sign anyone in.
          </p>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <strong className="small" style={{ marginRight: 'auto' }}>
              Nodes ({i.hosts.length})
            </strong>
            <button type="button" onClick={() =>
              setHostForm({ instanceId: i.id, hostname: '', ips: '' })}>Add node</button>
          </div>
          <table className="wide-table">
            <thead>
              <tr>
                <th>Hostname</th><th>From</th><th>Last sign-in seen</th><th></th>
              </tr>
            </thead>
            <tbody>
              {i.hosts.map(h => (
                <tr key={h.id}>
                  <td className="monospace small">{h.hostname}</td>
                  <td className="monospace small">
                    {h.allowed_from_ips.length ? h.allowed_from_ips.join(', ')
                      : <span style={{ opacity: 0.5 }}>any</span>}
                  </td>
                  <td className="small">
                    {h.last_seen_at ? formatDateTime(h.last_seen_at) : <span style={{ opacity: 0.5 }}>never</span>}
                    {h.last_seen_ip && <span className="monospace"> ({h.last_seen_ip})</span>}
                  </td>
                  <td>
                    <button type="button" onClick={() => setHostForm({
                      instanceId: i.id, id: h.id, hostname: h.hostname,
                      ips: h.allowed_from_ips.join(', '),
                    })}>Edit</button>{' '}
                    <button type="button" onClick={() => removeHost(h)}>Remove</button>
                  </td>
                </tr>
              ))}
              {i.hosts.length === 0 && (
                <tr><td colSpan={4} className="small" style={{ color: '#c0392b' }}>
                  No nodes &mdash; this cluster cannot be identified.
                </td></tr>
              )}
            </tbody>
          </table>

          {hostForm?.instanceId === i.id && (
            <div style={{ display: 'grid', gap: '6px', maxWidth: '480px', marginTop: '8px' }}>
              <label className="small">Hostname
                <input value={hostForm.hostname}
                  onChange={e => setHostForm({ ...hostForm, hostname: e.target.value })} />
              </label>
              <label className="small">Source addresses, comma separated
                <input value={hostForm.ips}
                  onChange={e => setHostForm({ ...hostForm, ips: e.target.value })} />
              </label>
              <div>
                <button type="button" onClick={saveHost}>Save node</button>{' '}
                <button type="button" onClick={() => setHostForm(null)}>Cancel</button>
              </div>
            </div>
          )}

          <p className="small" style={{ margin: '10px 0 4px' }}>
            {nf.format(i.usage.requests)} request{i.usage.requests === 1 ? '' : 's'}
            {i.usage.refused > 0 && (
              <span style={{ color: '#c0392b' }}>, {nf.format(i.usage.refused)} refused</span>
            )}
            {' · '}{nf.format(i.usage.people)} {i.usage.people === 1 ? 'person' : 'people'}
            {' · '}last seen {i.usage.last_seen ? formatDateTime(i.usage.last_seen) : 'never'}
            {' · '}
            <button type="button" style={{ padding: '1px 8px' }}
              onClick={() => setShowTokens(showTokens === i.id ? null : i.id)}>
              {i.tokens.length} live token{i.tokens.length === 1 ? '' : 's'}
            </button>
          </p>

          {showTokens === i.id && (
            <>
              <table className="wide-table">
                <thead>
                  <tr>
                    <th>Signed in</th><th>Role</th><th>Signed in via</th>
                    <th>Pinned to</th><th>Last used</th><th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {i.tokens.map(t => (
                    <tr key={`${t.party_uid}-${t.issued_at}`}>
                      <td className="monospace small">{t.party_uid}</td>
                      <td className="small">{t.scope || '—'}</td>
                      <td className="monospace small">{t.minted_via || '—'}</td>
                      <td className="monospace small">
                        {t.bound_ip || <span style={{ opacity: 0.5 }}>not used yet</span>}
                      </td>
                      <td className="small">{t.last_used_at ? formatDateTime(t.last_used_at) : '—'}</td>
                      <td className="small">{formatDateTime(t.expires_at)}</td>
                    </tr>
                  ))}
                  {i.tokens.length === 0 && (
                    <tr><td colSpan={6} className="small">No live tokens.</td></tr>
                  )}
                </tbody>
              </table>
              <p className="small" style={{ opacity: 0.7 }}>
                These are this platform&rsquo;s own short-lived tokens, not OAuth refresh
                tokens &mdash; there are none to revoke and reissue; each simply expires and
                the app mints another through the broker. Each token is <em>pinned</em> to
                the first address that presents it &mdash; normally the user&rsquo;s own
                browser, moments after sign-in &mdash; and is refused from anywhere else,
                which makes the app re-mint. A token lifted and replayed elsewhere fails,
                because minting needs the user&rsquo;s live Horizon session.
              </p>
            </>
          )}
        </div>
      ))}

      {!loading && instances.length === 0 && !error && (
        <p className="small">No clusters registered yet.</p>
      )}
    </div>
  );
};

export default HorizonInstancesPanel;
